import { Box, IconButton, Checkbox, Alert } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { Link } from "react-router-dom";
import { getSearchLink } from "./SavedSearchesPage";
import { DynamiResourceListView } from "./searches/SavedSearches";
import { useLanguage } from "../../Language";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";

export interface SavedSearchesMobileGridProps {
  displayedSearches: DynamiResourceListView[];
  selectedRows: number[];
  handleClick: (
    event: React.MouseEvent<unknown, MouseEvent>,
    row: DynamiResourceListView
  ) => void;
  setListToRename: React.Dispatch<
    React.SetStateAction<DynamiResourceListView | null>
  >;
  setListToDelete: React.Dispatch<
    React.SetStateAction<DynamiResourceListView | null>
  >;
  translateFacetName: (facetName: string) => string;
}

export const SavedSearchesMobileGrid: React.FC<
  SavedSearchesMobileGridProps
> = ({
  displayedSearches,
  selectedRows,
  handleClick,
  setListToRename,
  setListToDelete,
  translateFacetName,
}) => {
  const { translate } = useLanguage();

  return (
    <Box>
      {displayedSearches.length > 0 &&
        displayedSearches.map((search, index) => {
          const isItemSelected = selectedRows.indexOf(search.id) !== -1;
          const labelId = `table-checkbox-${index}`;

          return (
            <Box
              key={`row-${index}`}
              role="button"
              onClick={(event) => handleClick(event, search)}
              sx={{
                width: "100%",
                display: "flex",
                py: 3,
                flexDirection: "row",
                alignItems: "flex-start",
                borderBottom: "1px solid var(--mui-palette-TableCell-border)",
                backgroundColor: isItemSelected
                  ? "rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-selectedOpacity))"
                  : "#fff",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  mr: 1,
                  maxWidth: 120,
                  flex: 1,
                  minHeight: 0,
                  justifyContent: "space-between",
                  textAlign: "center",
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexGrow: 1,
                  }}
                >
                  <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    slotProps={{
                      input: {
                        "aria-label": labelId,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 0",
                  minWidth: 0,
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                }}
              >
                <Box>
                  <Link to={getSearchLink(search)} style={{ paddingBottom: 5 }}>
                    {search.name}
                  </Link>

                  <IconButton
                    aria-label={translate("rename")}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent row click
                      setListToRename(search);
                    }}
                    sx={{ mr: 3 }} // ensures rename icon wraps
                  >
                    <DriveFileRenameOutlineIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    overflowX: "visible",
                    textWrap: "wrap",
                    maxWidth: "85%",
                    minWidth: 0,
                  }}
                >
                  <strong>
                    {translate("searchQuery")}
                    {": "}
                  </strong>
                  {search.advanced
                    ? `(${translate("advancedSearch")})`
                    : search.expression}
                  {search.facetFilters.length > 0 ? ", " : ""}
                  {(search.facetFilters || []).map((filter, index) => (
                    <React.Fragment key={filter.id}>
                      {translateFacetName(filter.facetName)}
                      {": "}
                      {filter.facetValue}
                      {index < search.facetFilters.length - 1 && ", "}
                    </React.Fragment>
                  ))}
                </Box>
                <Box>
                  <strong>{translate("sortedBy")}:</strong>{" "}
                  {translate(search.sortType)}
                </Box>
                <Box>
                  <strong>{translate("date")}: </strong>
                  {dayjs(search.creationDate).format("MM-DD-YYYY")}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    justifyContent: "flex-start",
                  }}
                >
                  <IconButton
                    component={"a"}
                    target="_blank"
                    href={`/list/dynamic/${search.id}/rss`}
                    aria-label="RSS Feed"
                  >
                    <RssFeedIcon />
                  </IconButton>
                  <IconButton
                    aria-label={translate("remove")}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent row click
                      setListToDelete(search);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          );
        })}
      {displayedSearches.length < 1 && (
        <Alert severity="info">{translate("noSavedSearchesMessage")}</Alert>
      )}
    </Box>
  );
};
